import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "../../lib/supabase";
import PremiumHero from "./components/PremiumHero";
import OnboardingSidebar from "./components/OnboardingSidebar";
import { PremiumOnboardingCallDetails, PremiumCRMDetails } from "./components/PremiumSteps";

const PLAN_INFO: Record<string, { name: string; minutes: string }> = {
  basico: { name: "Básico", minutes: "500 minutos/mes" },
  profesional: { name: "Profesional", minutes: "1.500 minutos/mes" },
  premium: { name: "Premium", minutes: "Minutos ilimitados" },
};

interface DivertCode {
  label: string;
  code: string;
  note: string;
}

interface Step {
  id: number;
  icon: string;
  title: string;
  description: string;
  time: string;
  optional?: boolean;
  action?: string;
  actionLabel?: string;
  details: React.ReactNode;
}

const DIVERT_CODES: DivertCode[] = [
  {
    label: "Todas las llamadas (incondicional)",
    code: "**21*[TU_NUMERO_AURIA]#",
    note: "La IA responde siempre, incluso si estás disponible",
  },
  {
    label: "Cuando no contestas (recomendado)",
    code: "**61*[TU_NUMERO_AURIA]*11*20#",
    note: "La IA toma el relevo tras 20 segundos sin respuesta",
  },
  {
    label: "Cuando estás ocupado",
    code: "**67*[TU_NUMERO_AURIA]#",
    note: "La IA responde si ya estás en otra llamada",
  },
  {
    label: "Cancelar todos los desvíos",
    code: "##002#",
    note: "Desactiva cualquier desvío activo",
  },
];

interface PbxSystem {
  id: string;
  label: string;
  icon: string;
  steps: { title: string; body: string }[];
  note?: string;
}

const PBX_SYSTEMS: PbxSystem[] = [
  {
    id: "3cx",
    label: "3CX",
    icon: "ri-building-2-line",
    steps: [
      { title: "Accede al panel de 3CX", body: "Abre tu navegador y entra en la URL de tu panel 3CX (normalmente algo como https://tudominio.3cx.eu o la IP local de tu servidor). Inicia sesión como administrador." },
      { title: "Ve a Extensiones", body: "En el menú lateral izquierdo, haz clic en «Usuarios / Extensiones». Aparece la lista de todas las extensiones de tu centralita." },
      { title: "Edita la extensión que recibe llamadas", body: "Haz clic en el lápiz de la extensión a la que llegan los clientes. Se abre la configuración de esa extensión." },
      { title: "Configura el desvío por no contestar", body: "Busca la pestaña «Reenviar/Desviar llamadas» (Forwarding Rules). Localiza la opción «No disponible / Sin contestar» (No Answer). Cambia el destino a «Número externo» e introduce el número de teléfono Auria que aparece en tu email de bienvenida. Pon el timeout en 20 segundos." },
      { title: "Configura el desvío cuando estás ocupado", body: "En la misma pestaña, busca «Ocupado» (Busy). Establece el mismo número Auria como destino externo. Esto cubre el caso en que ya estás hablando por otra línea." },
      { title: "Guarda y aplica", body: "Haz clic en «Guardar» (Save) y luego en «Aplicar» (Apply) en la barra superior azul. Los cambios tardan unos segundos en activarse. Haz una llamada de prueba para verificarlo." },
    ],
    note: "Si usas 3CX Cloud (versión alojada en la nube de 3CX), el proceso es idéntico pero accedes desde https://tusubdominio.3cx.us o la URL que te envió 3CX al activar la cuenta.",
  },
  {
    id: "freepbx",
    label: "FreePBX / Asterisk",
    icon: "ri-terminal-box-line",
    steps: [
      { title: "Accede a FreePBX Admin", body: "Abre tu navegador y entra en la IP de tu servidor (p.ej. http://192.168.1.10). Ve a Admin → FreePBX Administration. Inicia sesión con tu usuario administrador." },
      { title: "Crea un troncal SIP hacia Auria (opcional)", body: "Si quieres desviar como ruta externa: ve a Connectivity → Trunks → Add Trunk → SIP. Asígnale un nombre «Auria». En el campo «dial» pon el número Auria con prefijo de salida. Si tu salida tiene prefijo 0, pon: 0[NUMERO_AURIA]. Guarda." },
      { title: "Edita la extensión que recibe llamadas", body: "Ve a Applications → Extensions. Haz clic en la extensión de tu línea principal (o la del teléfono del recepcionista). Se abre la ficha de configuración." },
      { title: "Configura el Follow-Me (recomendado)", body: "Baja hasta la sección «Follow Me». Actívala. En la lista de números, añade el número Auria. Ajusta el tiempo de ring a 20 segundos. Marca «Enable Follow Me» y guarda." },
      { title: "Alternativa: desvío directo por Feature Codes", body: "Si prefieres un desvío puro, usa Feature Codes: desde cualquier teléfono de la centralita, marca *72[NUMERO_AURIA] para activar desvío incondicional y *73 para cancelarlo." },
      { title: "Aplica la configuración", body: "Haz clic en el botón rojo «Apply Config» en la barra superior de FreePBX. Los cambios se aplican en tiempo real. Llama desde un móvil externo para comprobar que Auria responde." },
    ],
    note: "Si tienes Asterisk puro (sin interfaz gráfica), edita /etc/asterisk/extensions.conf y añade: exten => [EXTENSION],n,Dial(SIP/[NUMERO_AURIA],20) en el contexto correcto. Luego recarga con: asterisk -rx 'dialplan reload'.",
  },
  {
    id: "grandstream",
    label: "Grandstream UCM",
    icon: "ri-router-line",
    steps: [
      { title: "Accede al panel UCM", body: "Abre tu navegador y entra en la IP del equipo Grandstream UCM (normalmente 192.168.2.1 o la que te indicó el instalador). Ve a la interfaz web de administración e inicia sesión." },
      { title: "Ve a PBX → Extensiones", body: "En el menú lateral, navega a PBX Settings → Extensions. Localiza la extensión que recibe las llamadas entrantes de clientes y haz clic en el lápiz para editarla." },
      { title: "Activa el desvío en la ficha de extensión", body: "En la extensión, busca la sección «Call Forward». Activa el switch de «No Answer Forward». Pon el número Auria en el campo de destino. Ajusta «No Answer Timeout» a 20 segundos. Guarda." },
      { title: "Configura también «Busy Forward»", body: "En la misma sección «Call Forward», activa «Busy Forward» con el mismo número Auria. Así, si estás ocupado con otra llamada, la IA también responde." },
      { title: "Verifica la ruta de salida", body: "Asegúrate de que el número Auria puede salir por tu troncal. Ve a PBX → Outbound Routes y comprueba que el patrón de marcación incluye el número Auria o el prefijo que uses para llamadas externas." },
      { title: "Guarda y prueba", body: "Haz clic en «Save» y luego en el botón «Apply Changes» que aparece arriba. Llama desde un móvil externo y espera 20 segundos sin contestar — Auria debería responder." },
    ],
    note: "En Grandstream GXP/GXV (teléfonos físicos sin UCM), el desvío se hace directamente desde el teléfono: Menu → Call Features → Call Forward → No Answer → introduce el número Auria.",
  },
  {
    id: "voip",
    label: "Teléfono IP / App VoIP",
    icon: "ri-smartphone-line",
    steps: [
      { title: "Identifica tu app o teléfono", body: "Los más habituales en España son: Zoiper, Linphone, MicroSIP, Bria, teléfonos Yealink, Snom o Fanvil. El proceso es similar en todos." },
      { title: "Accede a los ajustes de la cuenta SIP", body: "En Zoiper: Ajustes → Cuentas → edita tu cuenta SIP. En Linphone: Ajustes → Cuenta → ficha de tu cuenta. En Yealink físico: accede a la IP del teléfono desde el navegador → Cuenta → edita la cuenta." },
      { title: "Busca «Call Forward» o «Desvío de llamadas»", body: "Dentro de los ajustes de la cuenta o del teléfono, busca la sección «Call Forward», «Forwarding» o «Desvío». Suele estar en la parte inferior de los ajustes de cuenta." },
      { title: "Activa «No Answer» con el número Auria", body: "Habilita el desvío por no contestar (No Answer / Unavailable). En el campo de número de destino, introduce el número Auria exactamente como aparece en tu email (con código de país si es necesario, ej: +34XXXXXXXXX). Ajusta el tiempo a 20 segundos." },
      { title: "Activa también «Busy» con el mismo número", body: "Habilita el desvío por ocupado (Busy Forward). Pon el mismo número Auria. Así la IA también responde cuando ya estás en otra llamada." },
      { title: "Guarda y reinicia la app si es necesario", body: "En apps móviles (Zoiper, Linphone) guarda los ajustes y cierra/abre la app. En teléfonos físicos Yealink/Snom haz clic en «Confirm» desde la web del teléfono. Llama desde otro móvil para comprobar que funciona." },
    ],
    note: "Si usas un proveedor VoIP como Masvoz, Telovoz, Neotel o Fonvirtual, el desvío se configura directamente en el panel web de tu proveedor (sin tocar el teléfono). Busca en su panel la opción «Desvíos» o «Enrutamiento» y añade el número Auria como destino de «no contestar».",
  },
];

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-1.5 px-3 py-1.5 bg-stone-100 hover:bg-stone-200 rounded-lg text-xs font-semibold text-stone-600 transition-colors cursor-pointer whitespace-nowrap"
    >
      <i className={copied ? "ri-check-line text-emerald-500" : "ri-clipboard-line"} />
      {copied ? "¡Copiado!" : "Copiar código"}
    </button>
  );
}

function CanvasMemoryForm() {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const { t } = useTranslation();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSending(true);
    setError("");
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as URLSearchParams);
    try {
      const res = await fetch("https://readdy.ai/api/form/d6vbd7t1l1ubpb129e40", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) {
        setSent(true);
      } else {
        setError(t("register_error"));
      }
    } catch {
      setError(t("register_error"));
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="flex flex-col items-center justify-center py-8 px-4 text-center bg-emerald-50 border border-emerald-100 rounded-2xl">
        <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-3">
          <i className="ri-check-line text-emerald-500 text-2xl" />
        </div>
        <p className="text-sm font-bold text-stone-800 mb-1">{t("cm_success_title")}</p>
        <p className="text-xs text-stone-500 leading-relaxed max-w-xs">{t("cm_success_desc")}</p>
      </div>
    );
  }

  const inputClass = "w-full px-3 py-2 text-sm bg-white border border-stone-200 rounded-xl text-stone-700 placeholder-stone-300 focus:outline-none focus:border-amber-300 focus:ring-1 focus:ring-amber-200 transition-all";
  const labelClass = "block text-xs font-bold text-stone-600 mb-1";
  const sectionClass = "pt-4 border-t border-stone-100 first:pt-0 first:border-t-0";

  return (
    <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
      <div className={sectionClass}>
        <p className="text-xs font-bold text-stone-400 uppercase tracking-widest mb-3">{t("cm_title_business")}</p>
        <div className="space-y-3">
          <div>
            <label htmlFor="cm_nombre" className={labelClass}>{t("cm_business_name_label")}</label>
            <input id="cm_nombre" name="nombre_negocio" type="text" required placeholder={t("cm_business_name_placeholder")} className={inputClass} />
          </div>
          <div>
            <label htmlFor="cm_tipo" className={labelClass}>{t("cm_business_type_label")}</label>
            <input id="cm_tipo" name="tipo_negocio" type="text" required placeholder={t("cm_business_type_placeholder")} className={inputClass} />
          </div>
          <div>
            <label htmlFor="cm_email" className={labelClass}>{t("cm_email_contact_label")}</label>
            <input id="cm_email" name="email" type="email" required placeholder="tu@email.com" className={inputClass} />
          </div>
          <div>
            <label htmlFor="cm_dir" className={labelClass}>{t("cm_address_label")}</label>
            <input id="cm_dir" name="direccion" type="text" placeholder={t("cm_address_placeholder")} className={inputClass} />
          </div>
        </div>
      </div>
      <div className={sectionClass}>
        <p className="text-xs font-bold text-stone-400 uppercase tracking-widest mb-3">{t("cm_title_schedule")}</p>
        <div className="space-y-3">
          <div>
            <label htmlFor="cm_hlv" className={labelClass}>{t("cm_mon_fri_label")}</label>
            <input id="cm_hlv" name="horario_lv" type="text" required placeholder={t("cm_mon_fri_placeholder")} className={inputClass} />
          </div>
          <div>
            <label htmlFor="cm_hsab" className={labelClass}>{t("cm_sat_label")}</label>
            <input id="cm_hsab" name="horario_sabado" type="text" required placeholder={t("cm_sat_placeholder")} className={inputClass} />
          </div>
          <div>
            <label htmlFor="cm_hdom" className={labelClass}>{t("cm_sun_label")}</label>
            <input id="cm_hdom" name="horario_domingo" type="text" required placeholder={t("cm_sun_placeholder")} className={inputClass} />
          </div>
        </div>
      </div>
      <div className={sectionClass}>
        <p className="text-xs font-bold text-stone-400 uppercase tracking-widest mb-3">{t("cm_title_services")}</p>
        <div className="space-y-3">
          {[1, 2, 3].map((n) => (
            <div key={n} className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <label htmlFor={`cm_srv${n}`} className={labelClass}>{t("cm_service_label")} {n}{n === 1 ? " *" : ""}</label>
                <input id={`cm_srv${n}`} name={`servicio_${n}`} type="text" required={n === 1} placeholder={t("cm_service_name_placeholder")} className={inputClass} />
              </div>
              <div>
                <label htmlFor={`cm_prx${n}`} className={labelClass}>{t("cm_price_label")}</label>
                <input id={`cm_prx${n}`} name={`precio_${n}`} type="text" required placeholder={t("cm_price_placeholder")} className={inputClass} />
              </div>
            </div>
          ))}
          <div>
            <label htmlFor="cm_otros_srv" className={labelClass}>{t("cm_other_services_label")}</label>
            <textarea id="cm_otros_srv" name="otros_servicios" rows={2} maxLength={500} placeholder={t("cm_other_services_placeholder")} className={`${inputClass} resize-none`} />
          </div>
        </div>
      </div>
      <div className={sectionClass}>
        <p className="text-xs font-bold text-stone-400 uppercase tracking-widest mb-3">{t("cm_title_appointments")}</p>
        <div className="space-y-3">
          <div>
            <label htmlFor="cm_dur" className={labelClass}>{t("cm_duration_label")}</label>
            <input id="cm_dur" name="duracion_cita" type="text" required placeholder={t("cm_duration_placeholder")} className={inputClass} />
          </div>
          <div>
            <label htmlFor="cm_conf" className={labelClass}>{t("cm_confirmation_label")}</label>
            <select id="cm_conf" name="confirmacion_24h" className={inputClass}>
              <option value="">{t("cm_select_option")}</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </div>
          <div>
            <label htmlFor="cm_cancel" className={labelClass}>{t("cm_cancellation_label")}</label>
            <input id="cm_cancel" name="politica_cancelacion" type="text" required placeholder={t("cm_cancellation_placeholder")} className={inputClass} />
          </div>
        </div>
      </div>
      <div className={sectionClass}>
        <p className="text-xs font-bold text-stone-400 uppercase tracking-widest mb-3">{t("cm_title_faq")}</p>
        <div className="space-y-4">
          {[1, 2, 3].map((n) => (
            <div key={n} className="space-y-1.5">
              <label className={labelClass}>{t("cm_question_label")} {n}</label>
              <input name={`faq_pregunta_${n}`} type="text" required placeholder={t("cm_question_placeholder")} className={inputClass} />
              <input name={`faq_respuesta_${n}`} type="text" required placeholder={t("cm_answer_placeholder")} className={inputClass} />
            </div>
          ))}
        </div>
      </div>
      <div className={sectionClass}>
        <label htmlFor="cm_extra" className={labelClass}>{t("cm_title_extra")}</label>
        <textarea id="cm_extra" name="informacion_adicional" rows={3} maxLength={500} placeholder={t("cm_extra_placeholder")} className={`${inputClass} resize-none`} />
        <p className="text-xs text-stone-300 mt-1">{t("cm_max_chars")}</p>
      </div>
      {error && (
        <p className="text-xs text-red-500 flex items-center gap-1.5">
          <i className="ri-error-warning-line" />
          {error}
        </p>
      )}
      <button
        type="submit"
        disabled={sending}
        className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-amber-500 hover:bg-amber-400 disabled:bg-stone-200 disabled:text-stone-400 text-white text-sm font-bold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
      >
        {sending ? (
          <>
            <i className="ri-loader-4-line animate-spin" />
            {t("cm_sending")}
          </>
        ) : (
          <>
            <i className="ri-send-plane-line" />
            {t("cm_submit_btn")}
          </>
        )}
      </button>
    </form>
  );
}

function VoipGuide() {
  const [activeSystem, setActiveSystem] = useState<string>("3cx");
  const system = PBX_SYSTEMS.find((s) => s.id === activeSystem)!;

  return (
    <div className="mt-4 border border-stone-200 rounded-2xl overflow-hidden">
      <div className="flex overflow-x-auto bg-stone-100 border-b border-stone-200">
        {PBX_SYSTEMS.map((s) => (
          <button
            key={s.id}
            onClick={() => setActiveSystem(s.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-xs font-bold whitespace-nowrap transition-colors cursor-pointer border-b-2 ${
              activeSystem === s.id
                ? "bg-white border-amber-500 text-amber-600"
                : "border-transparent text-stone-500 hover:text-stone-700 hover:bg-stone-50"
            }`}
          >
            <i className={`${s.icon} text-sm`} />
            {s.label}
          </button>
        ))}
      </div>
      <div className="bg-white p-5 space-y-4">
        <ol className="space-y-4">
          {system.steps.map((step, i) => (
            <li key={i} className="flex gap-4">
              <div className="flex-shrink-0 w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center text-xs font-bold text-amber-700">
                {i + 1}
              </div>
              <div className="flex-1 min-w-0 pt-0.5">
                <p className="text-sm font-bold text-stone-800 mb-1">{step.title}</p>
                <p className="text-xs text-stone-500 leading-relaxed">{step.body}</p>
              </div>
            </li>
          ))}
        </ol>
        {system.note && (
          <div className="p-3 bg-stone-50 border border-stone-100 rounded-xl flex items-start gap-3 mt-2">
            <i className="ri-lightbulb-line text-amber-500 text-sm flex-shrink-0 mt-0.5" />
            <p className="text-xs text-stone-500 leading-relaxed">{system.note}</p>
          </div>
        )}
        <div className="pt-2 border-t border-stone-100 flex items-center justify-between">
          <p className="text-xs text-stone-400">¿Aún tienes dudas con tu sistema específico?</p>
          <a
            href="mailto:soporteauria@gmail.com?subject=Ayuda desvio centralita IP"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-600 hover:text-amber-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-mail-line" />
            Contactar soporte
          </a>
        </div>
      </div>
    </div>
  );
}

function ConfettiParticle({ index }: { index: number }) {
  const colors = ["#F59E0B", "#10B981", "#6366F1", "#EC4899", "#3B82F6", "#F97316", "#14B8A6"];
  const color = colors[index % colors.length];
  const left = `${(index * 7 + 5) % 100}%`;
  const delay = `${(index * 0.15) % 2}s`;
  const duration = `${2.5 + (index % 3) * 0.5}s`;
  const size = index % 3 === 0 ? 8 : index % 2 === 0 ? 6 : 10;
  return (
    <div
      className="absolute top-0 rounded-sm animate-confetti pointer-events-none"
      style={{
        left,
        width: size,
        height: size * (index % 2 === 0 ? 1 : 2.5),
        backgroundColor: color,
        animationDelay: delay,
        animationDuration: duration,
        opacity: 0,
      }}
    />
  );
}

export default function BienvenidaPage() {
  const [searchParams] = useSearchParams();
  const { i18n, t } = useTranslation();
  const isEn = i18n.language === "en";
  const planId = searchParams.get("plan") ?? "profesional";
  const clientEmail = searchParams.get("email") ?? "";
  const plan = PLAN_INFO[planId] ?? PLAN_INFO.profesional;
  const isPremium = planId === "premium";
  const storageKey = clientEmail ? `auria_setup_${clientEmail}` : `auria_setup_guest_${planId}`;
  const [checked, setChecked] = useState<Set<number>>(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) return new Set<number>(JSON.parse(saved) as number[]);
    } catch { /* ignore */ }
    return new Set<number>();
  });
  const [expanded, setExpanded] = useState<number | null>(null);
  const [entered, setEntered] = useState(false);
  const [callBooked, setCallBooked] = useState(false);
  const [setupSaved, setSetupSaved] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    timerRef.current = setTimeout(() => setEntered(true), 100);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // Persist completed steps to localStorage
  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify([...checked])); } catch { /* ignore */ }
  }, [checked, storageKey]);

  const toggleStep = (id: number) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleExpand = (id: number) => {
    setExpanded((prev) => (prev === id ? null : id));
    // On mobile, scroll to step
    setTimeout(() => {
      const el = document.getElementById(`step-${id}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const handleSidebarStepClick = (id: number) => {
    setExpanded((prev) => (prev === id ? null : id));
    setTimeout(() => {
      const el = document.getElementById(`step-${id}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const premiumOnboardingStep: Step = {
    id: 6,
    icon: "ri-video-chat-line",
    title: "Reserva tu llamada de onboarding",
    description: "Tu account manager te llamará en la franja que elijas para configurar juntos Canvas Memory, Branded Call ID, desvíos y CRM. Tú no tienes que hacer nada técnico.",
    time: "30 min",
    details: (
      <PremiumOnboardingCallDetails
        onBooked={() => {
          setCallBooked(true);
          toggleStep(6);
        }}
      />
    ),
  };

  const premiumCRMStep: Step = {
    id: 7,
    icon: "ri-database-2-line",
    title: "Integración CRM con FastMCP",
    description: "Conecta tu CRM (HubSpot, Salesforce, Zoho, Pipedrive...) para que Auria sincronice leads, citas y transcripciones automáticamente. Lo configuramos en la llamada.",
    time: "En llamada",
    details: <PremiumCRMDetails />,
  };

  const baseSteps: Step[] = [
    {
      id: 1,
      icon: "ri-mail-check-line",
      title: "Confirma tu email",
      description: "Revisa tu bandeja de entrada. Te hemos enviado un email de bienvenida con los detalles de tu suscripción y factura.",
      time: "1 min",
      details: (
        <div className="space-y-3">
          <p className="text-xs font-bold text-stone-500 uppercase tracking-widest">Cómo hacerlo</p>
          <ol className="space-y-2">
            {[
              { icon: "ri-search-line", text: "Busca un email de auriareceptionistai@gmail.com en tu bandeja de entrada" },
              { icon: "ri-spam-2-line", text: "Si no aparece, revisa la carpeta de Spam o Promociones (Gmail)" },
              { icon: "ri-file-text-line", text: "El email contiene tu factura y los detalles del plan contratado" },
              { icon: "ri-bookmark-line", text: "Guárdalo — contiene tu número de teléfono asignado cuando esté listo" },
            ].map((item, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-amber-100 flex-shrink-0 mt-0.5">
                  <i className={`${item.icon} text-amber-600 text-xs`} />
                </div>
                <span className="text-sm text-stone-600 leading-relaxed">{item.text}</span>
              </li>
            ))}
          </ol>
        </div>
      ),
    },
    {
      id: 2,
      icon: "ri-calendar-2-line",
      title: "Conecta Google Calendar",
      description: "Vincula tu agenda para que Auria pueda comprobar disponibilidad y agendar citas en tiempo real sin conflictos.",
      time: "2 min",
      action: "https://calendar.google.com",
      actionLabel: "Abrir Google Calendar",
      details: (
        <div className="space-y-3">
          <p className="text-xs font-bold text-stone-500 uppercase tracking-widest">Cómo hacerlo</p>
          <ol className="space-y-2">
            {[
              { icon: "ri-google-line", text: "Abre Google Calendar y asegúrate de estar en la cuenta correcta" },
              { icon: "ri-calendar-check-line", text: "Comprueba que el calendario donde guardas tus citas está activado (visible)" },
              { icon: "ri-settings-3-line", text: "En Configuración → Ajustes de calendario, confirma que el calendario es público o compartido" },
              { icon: "ri-link-m", text: "Auria se conecta automáticamente a tu calendario al configurar tu primer flujo de citas" },
              { icon: "ri-information-line", text: "Si tienes varias cuentas de Google, usa siempre la misma durante la configuración" },
            ].map((item, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-amber-100 flex-shrink-0 mt-0.5">
                  <i className={`${item.icon} text-amber-600 text-xs`} />
                </div>
                <span className="text-sm text-stone-600 leading-relaxed">{item.text}</span>
              </li>
            ))}
          </ol>
          <a
            href="https://calendar.google.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-2 px-4 py-2 bg-stone-900 text-white text-xs font-semibold rounded-xl transition-colors hover:bg-stone-800 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-external-link-line" />
            Abrir Google Calendar
          </a>
        </div>
      ),
    },
    {
      id: 3,
      icon: "ri-file-text-line",
      title: isEn ? "Set up your Canvas Memory" : "Configura el Canvas Memory",
      description: isEn
        ? "Fill in the form with your business data: hours, services, prices and customer care protocol. We'll configure it in under 24h."
        : "Rellena el formulario con los datos de tu negocio: horarios, servicios, precios y protocolo de atención. Lo configuramos en menos de 24h.",
      time: "5 min",
      details: (
        <div className="space-y-4">
          <div className="p-3 bg-amber-50 border border-amber-100 rounded-xl flex items-start gap-3">
            <i className="ri-lightbulb-line text-amber-500 text-base flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700 leading-relaxed">
              <strong>{isEn ? t("cm_hint_title") : "¿Para qué sirve esto?"}</strong>{" "}
              {isEn ? t("cm_hint_desc") : "El Canvas Memory es la memoria de tu IA. Cuanto más detallado lo rellenes, mejor responderá tu IA a las preguntas de tus clientes sobre horarios, precios y citas."}
            </p>
          </div>
          <CanvasMemoryForm />
        </div>
      ),
    },
    {
      id: 4,
      icon: "ri-phone-forward-line",
      title: "Activa el desvío de llamadas",
      description: "Introduce los códigos GSM desde tu móvil para redirigir llamadas a tu número Auria. Funciona en Movistar, Vodafone, Orange, MásMóvil, DIGI y la mayoría de operadoras.",
      time: "2 min",
      details: (
        <div className="space-y-5">
          <div className="p-3 bg-emerald-50 border border-emerald-100 rounded-xl flex items-start gap-3">
            <i className="ri-information-line text-amber-600 text-base flex-shrink-0 mt-0.5" />
            <p className="text-xs text-emerald-700 leading-relaxed">
              <strong>Antes de empezar:</strong> necesitas el número de teléfono que te hemos asignado. Lo encontrarás en el email de bienvenida o escríbenos a soporteauria@gmail.com para que te lo confirmemos.
            </p>
          </div>
          <div>
            <p className="text-xs font-bold text-stone-500 uppercase tracking-widest mb-3">
              Opción A — Móvil con SIM (Movistar, Vodafone, Orange, MásMóvil, DIGI...)
            </p>
            <p className="text-xs text-stone-400 mb-3 leading-relaxed">
              Desde tu móvil, abre el marcador de llamadas, escribe el código y pulsa el botón de llamar. El desvío se activa en segundos.
            </p>
            <div className="space-y-2">
              {DIVERT_CODES.map((item) => (
                <div key={item.label} className="bg-stone-50 border border-stone-100 rounded-xl p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-stone-700 mb-1">{item.label}</p>
                      <p className="text-xs text-stone-400 leading-relaxed">{item.note}</p>
                    </div>
                    <CopyButton text={item.code} />
                  </div>
                  <div className="mt-3 px-3 py-2 bg-stone-900 rounded-lg font-mono text-sm text-amber-400 tracking-widest">
                    {item.code}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <i className="ri-router-line text-stone-400 text-base" />
              <p className="text-xs font-bold text-stone-700 uppercase tracking-widest">
                Opción B — Centralita IP, VoIP o PBX
              </p>
            </div>
            <p className="text-xs text-stone-500 leading-relaxed mb-1">
              Selecciona tu sistema y sigue los pasos. No necesitas llamar a soporte — la guía cubre los pasos exactos dentro de cada panel.
            </p>
            <VoipGuide />
          </div>
        </div>
      ),
    },
    {
      id: 5,
      icon: "ri-phone-line",
      title: "Haz una llamada de prueba",
      description: "Verifica que todo funciona antes de considerarlo listo. Llama desde un móvil diferente y comprueba que Auria responde correctamente.",
      time: "3 min",
      optional: true,
      details: (
        <div className="space-y-5">
          <div className="p-3 bg-emerald-50 border border-emerald-100 rounded-xl flex items-start gap-3">
            <i className="ri-shield-check-line text-emerald-600 text-base flex-shrink-0 mt-0.5" />
            <p className="text-xs text-emerald-700 leading-relaxed">
              <strong>Recomendado antes de abrir tu negocio.</strong> Esta prueba confirma que el desvío está activo y que Auria atiende con el mensaje correcto. Solo lleva 3 minutos.
            </p>
          </div>
          <div>
            <p className="text-xs font-bold text-stone-500 uppercase tracking-widest mb-3">Cómo hacer la prueba</p>
            <ol className="space-y-4">
              {[
                { icon: "ri-smartphone-line", title: "Coge un móvil diferente al de tu negocio", body: "Usa tu móvil personal, el de un familiar o un compañero. No uses el mismo número desde el que activaste el desvío — ese no se desviará a sí mismo." },
                { icon: "ri-phone-line", title: "Llama al número de teléfono de tu negocio", body: "Marca tu número habitual de negocio (el que tienen tus clientes). Si configuraste desvío «cuando no contestas», no descuelgues y espera unos 20 segundos." },
                { icon: "ri-ear-line", title: "Escucha lo que responde Auria", body: "Deberías escuchar un saludo de voz con el nombre de tu negocio. Si aún no has enviado el Canvas Memory, Auria responderá con un mensaje genérico estándar — es correcto, funcionará mejor cuando reciba los datos." },
                { icon: "ri-chat-voice-line", title: "Interactúa con la IA", body: "Pregunta algo sencillo: «¿Cuál es el horario?» o «Quiero pedir cita». Verifica que responde de forma fluida y natural." },
                { icon: "ri-checkbox-circle-line", title: "Marca el paso como completado", body: "Si todo fue bien, marca este paso como completado. Tu recepcionista IA ya está 100% operativo y verificado." },
              ].map((item, idx) => (
                <li key={idx} className="flex gap-4">
                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center text-xs font-bold text-emerald-700">
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0 pt-0.5">
                    <p className="text-sm font-bold text-stone-800 mb-1">{item.title}</p>
                    <p className="text-xs text-stone-500 leading-relaxed">{item.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
          <div>
            <p className="text-xs font-bold text-stone-500 uppercase tracking-widest mb-3">Si algo no funciona</p>
            <div className="space-y-2">
              {[
                { problem: "No hay desvío — tu móvil sigue sonando normal", fix: "El código GSM no se aplicó correctamente. Vuelve al paso 4 y márcalo de nuevo. Puedes verificar el estado del desvío marcando *#61# desde tu móvil.", icon: "ri-phone-off-line", isRed: true },
                { problem: "Auria responde pero con datos incorrectos o genéricos", fix: "El Canvas Memory todavía no está configurado. Envía la plantilla del paso 3 a soporteauria@gmail.com — en menos de 24h la IA responderá con los datos de tu negocio.", icon: "ri-file-warning-line", isRed: false },
                { problem: "La llamada cae directamente al buzón de voz", fix: "Desactiva el buzón de voz de tu operadora antes de activar el desvío a Auria. Llama a tu operadora o busca en su app el ajuste «Buzón de voz» y desactívalo.", icon: "ri-voicemail-line", isRed: false },
                { problem: "Auria no contesta o se escucha silencio", fix: "Tu número Auria puede no estar activado todavía. Escríbenos a soporteauria@gmail.com con asunto «Número no activo» y lo activamos en menos de 2 horas.", icon: "ri-error-warning-line", isRed: true },
              ].map((item, idx) => (
                <div key={idx} className="bg-stone-50 border border-stone-100 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5 ${item.isRed ? "bg-red-50" : "bg-amber-50"}`}>
                      <i className={`${item.icon} text-xs ${item.isRed ? "text-red-500" : "text-amber-500"}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-stone-700 mb-1">{item.problem}</p>
                      <p className="text-xs text-stone-500 leading-relaxed">{item.fix}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-stone-100">
            <p className="text-xs text-stone-400">¿Otro problema no listado aquí?</p>
            <a
              href="mailto:soporteauria@gmail.com?subject=Problema llamada de prueba"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-600 hover:text-amber-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-line" />
              Contactar soporte
            </a>
          </div>
        </div>
      ),
    },
  ];

  const STEPS: Step[] = isPremium
    ? [baseSteps[0], premiumOnboardingStep, baseSteps[1], baseSteps[2], baseSteps[3], premiumCRMStep, baseSteps[4]]
    : baseSteps;

  const mandatorySteps = STEPS.filter((s) => !s.optional);
  const mandatoryDone = [...checked].filter((id) => mandatorySteps.some((s) => s.id === id)).length;
  const progress = Math.round((mandatoryDone / mandatorySteps.length) * 100);
  const mandatoryAllDone = mandatoryDone === mandatorySteps.length;
  const allDone = checked.size === STEPS.length;

  // Auto-mark setup_completed = true when all mandatory steps are done
  useEffect(() => {
    if (!mandatoryAllDone || setupSaved || !clientEmail) return;
    const markSetupComplete = async () => {
      await supabase
        .from("subscriptions")
        .update({ setup_completed: true })
        .eq("email", clientEmail.toLowerCase().trim());
      setSetupSaved(true);
    };
    markSetupComplete();
  }, [mandatoryAllDone, setupSaved, clientEmail]);

  return (
    <div className="min-h-screen bg-white flex flex-col overflow-x-hidden">
      <meta name="robots" content="noindex, nofollow" />

      <div className="fixed inset-0 pointer-events-none overflow-hidden z-10">
        {Array.from({ length: 28 }).map((_, idx) => (
          <ConfettiParticle key={idx} index={idx} />
        ))}
      </div>

      <nav className="relative z-20 w-full bg-white/80 backdrop-blur-sm border-b border-stone-100 px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link to="/" className="cursor-pointer">
            <img
              src="https://static.readdy.ai/image/464d6b2d88c2c0b329c43db37f7a6b21/5e3d82f418168fbdbc49efd95d2ee821.png"
              alt="Auria"
              className="h-8 w-auto"
            />
          </Link>
          <div className="flex items-center gap-3">
            <button
              onClick={() => i18n.changeLanguage(isEn ? "es" : "en")}
              className="text-xs font-bold px-3 py-1.5 rounded-full border border-stone-200 text-stone-500 hover:border-stone-400 hover:text-stone-700 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-translate-line text-sm" />
              {isEn ? "ES" : "EN"}
            </button>
            <Link
              to="/cuenta"
              className="flex items-center gap-2 text-sm text-stone-500 hover:text-stone-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-user-settings-line" />
              {isEn ? "Manage subscription" : "Gestionar suscripción"}
            </Link>
          </div>
        </div>
      </nav>

      <main className="relative z-20 flex-1 max-w-5xl mx-auto w-full px-6 py-14">
        <div className="lg:grid lg:grid-cols-[260px_1fr] lg:gap-10 lg:items-start">
          {/* Sidebar — desktop only */}
          <OnboardingSidebar
            steps={STEPS.map(({ id, icon, title, time, optional }) => ({ id, icon, title, time, optional }))}
            checked={checked}
            progress={progress}
            mandatoryDone={mandatoryDone}
            mandatoryTotal={mandatorySteps.length}
            mandatoryAllDone={mandatoryAllDone}
            expanded={expanded}
            onStepClick={handleSidebarStepClick}
            planName={plan.name}
          />

          {/* Main content */}
          <div>
        {isPremium ? (
          <PremiumHero entered={entered} />
        ) : (
          <div className={`text-center mb-12 transition-all duration-700 ${entered ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="relative inline-flex items-center justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-emerald-50 flex items-center justify-center">
                <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
                  <i className="ri-check-line text-3xl text-emerald-500 font-black" />
                </div>
              </div>
              <div className="absolute inset-0 rounded-full border-2 border-emerald-300 animate-ping opacity-20" />
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-50 border border-amber-100 rounded-full mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
              <span className="text-xs font-bold text-amber-600 uppercase tracking-widest">
                {isEn ? `${plan.name} plan activated` : `Plan ${plan.name} activado`}
              </span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-stone-900 mb-4 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              {isEn ? "Welcome to Auria" : "Bienvenido a Auria"}
            </h1>
            <p className="text-stone-500 text-lg max-w-xl mx-auto leading-relaxed">
              {isEn
                ? "Your AI receptionist is now active. Follow the steps below — each one has detailed instructions to set it up yourself in under 15 minutes."
                : "Tu recepcionista con IA ya está activo. Sigue los pasos de abajo — cada uno tiene instrucciones detalladas para hacerlo tú mismo en menos de 15 minutos."}
            </p>
            <div className="inline-flex items-center gap-6 mt-6 px-6 py-3 bg-stone-50 border border-stone-100 rounded-2xl">
              {[
                { icon: "ri-headphone-line", label: plan.minutes },
                { icon: "ri-time-line", label: "24/7/365" },
                { icon: "ri-global-line", label: isEn ? "31 languages" : "31 idiomas" },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2 text-sm text-stone-600">
                  <i className={`${item.icon} text-amber-500`} />
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className={`mb-8 transition-all duration-700 delay-150 ${entered ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-bold text-stone-700">
              {isEn ? "Setup progress" : "Progreso de configuración"}
            </span>
            <span className={`text-sm font-bold ${mandatoryAllDone ? "text-emerald-500" : "text-stone-400"}`}>
              {mandatoryDone}/{mandatorySteps.length} {isEn ? "steps completed" : "pasos completados"}
            </span>
          </div>
          <div className="w-full h-2 bg-stone-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${mandatoryAllDone ? "bg-emerald-400" : "bg-amber-400"}`}
              style={{ width: `${progress}%` }}
            />
          </div>
          {allDone && (
            <p className="text-emerald-500 text-xs font-semibold mt-2 flex items-center gap-1">
              <i className="ri-check-double-line" />
              {isEn ? "Setup complete! Your AI receptionist is 100% operational and verified." : "¡Configuración completa! Tu recepcionista IA está 100% operativo y verificado."}
            </p>
          )}
          {mandatoryAllDone && !allDone && (
            <p className="text-emerald-500 text-xs font-semibold mt-2 flex items-center gap-1">
              <i className="ri-check-line" />
              {isEn ? "Essential steps complete. Make the test call to verify everything." : "Pasos esenciales completos. Haz la llamada de prueba para verificarlo todo."}
            </p>
          )}
        </div>

        <div className="space-y-3">
          {STEPS.map((step, i) => {
            const isDone = checked.has(step.id);
            const isOpen = expanded === step.id;
            return (
              <div
                key={step.id}
                id={`step-${step.id}`}
                className={`border rounded-2xl transition-all duration-700 ${
                  isDone
                    ? "bg-emerald-50 border-emerald-100"
                    : step.optional
                    ? "bg-white border-dashed border-stone-200"
                    : "bg-white border-stone-100"
                } ${entered ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
                style={{ transitionDelay: `${200 + i * 80}ms` }}
              >
                <div className="p-5">
                  <div className="flex items-start gap-4">
                    <button
                      onClick={() => toggleStep(step.id)}
                      className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all cursor-pointer ${
                        isDone ? "bg-emerald-400 border-emerald-400" : "border-stone-300 hover:border-amber-400"
                      }`}
                      aria-label={isDone ? "Marcar como pendiente" : "Marcar como completado"}
                    >
                      {isDone && <i className="ri-check-line text-white text-xs" />}
                    </button>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className={`w-8 h-8 flex items-center justify-center rounded-xl flex-shrink-0 ${isDone ? "bg-emerald-100" : step.optional ? "bg-stone-50" : "bg-stone-100"}`}>
                            <i className={`${step.icon} text-sm ${isDone ? "text-emerald-500" : step.optional ? "text-stone-400" : "text-stone-500"}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className={`text-sm font-bold ${isDone ? "text-stone-400 line-through" : "text-stone-800"}`}>
                                {step.title}
                              </h3>
                              <span className="text-xs text-stone-400 whitespace-nowrap">~{step.time}</span>
                              {step.optional && !isDone && (
                                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-stone-100 text-stone-400 whitespace-nowrap">
                                  Opcional
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {!step.optional && (
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${isDone ? "bg-emerald-100 text-emerald-600" : "bg-stone-100 text-stone-400"}`}>
                              {i + 1}/{mandatorySteps.length}
                            </span>
                          )}
                          <button
                            onClick={() => toggleExpand(step.id)}
                            className={`w-7 h-7 flex items-center justify-center rounded-lg transition-all cursor-pointer ${isOpen ? "bg-amber-100 text-amber-600" : "bg-stone-100 text-stone-400 hover:bg-stone-200"}`}
                            aria-label={isOpen ? "Ocultar instrucciones" : "Ver instrucciones"}
                          >
                            <i className={`${isOpen ? "ri-subtract-line" : "ri-add-line"} text-sm`} />
                          </button>
                        </div>
                      </div>

                      <p className={`text-sm mt-2 leading-relaxed ${isDone ? "text-stone-400" : "text-stone-500"}`}>
                        {step.description}
                      </p>

                      {!isOpen && !isDone && (
                        <button
                          onClick={() => toggleExpand(step.id)}
                          className="mt-2 text-xs font-semibold text-amber-600 hover:text-amber-700 transition-colors cursor-pointer flex items-center gap-1"
                        >
                          <i className="ri-play-circle-line" />
                          Ver instrucciones paso a paso
                        </button>
                      )}
                    </div>
                  </div>

                  {isOpen && (
                    <div className="mt-5 ml-10 pt-5 border-t border-stone-100">
                      {step.details}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className={`mt-10 grid sm:grid-cols-2 gap-4 transition-all duration-700 delay-500 ${entered ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {isPremium ? (
            <>
              <div className="p-5 bg-amber-500 rounded-2xl text-white">
                <div className="w-9 h-9 flex items-center justify-center mb-3">
                  <i className="ri-vip-crown-2-line text-white text-xl" />
                </div>
                <h3 className="font-bold text-sm mb-1">Tu account manager dedicado</h3>
                <p className="text-amber-100 text-xs leading-relaxed mb-3">
                  Tienes un miembro del equipo Auria asignado exclusivamente a tu cuenta. Escríbele directamente para cualquier consulta.
                </p>
                <a
                  href="mailto:auriareceptionistai@gmail.com"
                  className="inline-flex items-center gap-2 text-xs font-semibold text-white hover:text-amber-200 transition-colors cursor-pointer"
                >
                  auriareceptionistai@gmail.com
                  <i className="ri-arrow-right-line" />
                </a>
              </div>
              <div className="p-5 bg-stone-900 rounded-2xl text-white">
                <div className="w-9 h-9 flex items-center justify-center mb-3">
                  <i className="ri-settings-3-line text-amber-400 text-xl" />
                </div>
                <h3 className="font-bold text-sm mb-1">Gestiona tu suscripción</h3>
                <p className="text-stone-400 text-xs leading-relaxed mb-3">
                  Accede a tus facturas, revisa tu plan Premium o actualiza tu método de pago cuando quieras.
                </p>
                <Link to="/cuenta" className="inline-flex items-center gap-2 text-xs font-semibold text-amber-400 hover:text-amber-300 transition-colors cursor-pointer">
                  Portal de cliente
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </>
          ) : (
            <>
              <div className="p-5 bg-stone-900 rounded-2xl text-white">
                <div className="w-9 h-9 flex items-center justify-center mb-3">
                  <i className="ri-customer-service-2-line text-amber-400 text-xl" />
                </div>
                <h3 className="font-bold text-sm mb-1">¿Algo no funciona?</h3>
                <p className="text-stone-400 text-xs leading-relaxed mb-3">
                  Si tienes algún problema durante la configuración, estamos aquí para ayudarte.
                </p>
                <a
                  href="mailto:soporteauria@gmail.com"
                  className="inline-flex items-center gap-2 text-xs font-semibold text-amber-400 hover:text-amber-300 transition-colors cursor-pointer"
                >
                  soporteauria@gmail.com
                  <i className="ri-arrow-right-line" />
                </a>
              </div>
              <div className="p-5 bg-amber-50 border border-amber-100 rounded-2xl">
                <div className="w-9 h-9 flex items-center justify-center mb-3">
                  <i className="ri-settings-3-line text-amber-600 text-xl" />
                </div>
                <h3 className="font-bold text-sm text-stone-800 mb-1">Gestiona tu suscripción</h3>
                <p className="text-stone-500 text-xs leading-relaxed mb-3">
                  Accede a tus facturas, cambia de plan o actualiza tu método de pago cuando quieras.
                </p>
                <Link to="/cuenta" className="inline-flex items-center gap-2 text-xs font-semibold text-amber-600 hover:text-amber-700 transition-colors cursor-pointer">
                  Portal de cliente
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </>
          )}
        </div>

        <div className="text-center mt-10 transition-all duration-700 delay-700" style={{ opacity: entered ? 1 : 0 }}>
          <Link to="/" className="text-sm text-stone-400 hover:text-stone-600 transition-colors cursor-pointer inline-flex items-center gap-1">
            <i className="ri-arrow-left-line text-xs" />
            {isEn ? "Back to main page" : "Volver a la página principal"}
          </Link>
        </div>

          </div>{/* end main content */}
        </div>{/* end grid */}
      </main>
    </div>
  );
}
